import { useEffect, useState } from 'react';
import KeyValueEditor from './KeyValueEditor.jsx';

/**
 * Demand Connectivity & Management workstream: credential-hygiene wrapper around
 * KeyValueEditor for demand-partner params (site IDs, tag IDs, zone IDs, etc).
 *
 * The task's explicit instruction is that saved credentials must never be displayed back in
 * plaintext after being saved. Two existing GET routes already return exact plaintext values
 * (GET /api/publishers/:id/demand-partners — covered by a real, passing test asserting that
 * exact contract — and the params this component receives may originate from there), so the fix
 * lives at the presentation layer rather than the API: this component never renders a saved
 * value into a visible or editable input. It shows only which parameter KEYS are already set
 * ("Configured: site_id, tag_id") behind an explicit "Replace credentials" action. Choosing to
 * replace opens a fresh KeyValueEditor seeded with those same key names but blank values — the
 * operator must re-enter every value, matching the underlying PUT routes' actual semantics
 * (they replace the whole params object, they don't merge), so this UI is honest about that
 * rather than implying a partial edit is possible.
 *
 * `existingKeys` — the param key names already saved (from a params object the caller already
 * has, or from a dedicated key-only GET route). `onSave(params)` receives a full replacement
 * params object when the operator submits.
 */
export default function MaskedParamsEditor({ existingKeys, onSave, disabled, emptyHint }) {
  const keys = existingKeys || [];
  const [editing, setEditing] = useState(keys.length === 0);
  const [draft, setDraft] = useState(() => Object.fromEntries(keys.map((k) => [k, ''])));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditing(keys.length === 0);
    setDraft(Object.fromEntries(keys.map((k) => [k, ''])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys.join('|')]);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="masked-params">
        <div className="masked-params__summary">
          <span className="masked-params__label">Configured:</span>{' '}
          {keys.map((k) => (
            <code key={k} className="masked-params__key">
              {k}=••••••
            </code>
          ))}
          <span className="masked-params__hint">(values hidden — never re-displayed once saved)</span>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" disabled={disabled} onClick={() => setEditing(true)}>
          Replace credentials
        </button>
      </div>
    );
  }

  return (
    <div className="masked-params masked-params--editing">
      {keys.length > 0 && (
        <p className="masked-params__hint">
          Re-enter every value below — this replaces the saved credentials entirely, it does not merge.
        </p>
      )}
      {!keys.length && emptyHint && <p className="card__hint">{emptyHint}</p>}
      <KeyValueEditor value={draft} onChange={setDraft} disabled={disabled || saving} />
      <div className="masked-params__actions">
        {keys.length > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={disabled || saving}
            onClick={() => {
              setDraft(Object.fromEntries(keys.map((k) => [k, ''])));
              setEditing(false);
            }}
          >
            Cancel
          </button>
        )}
        <button type="button" className="btn btn--primary btn--sm" disabled={disabled || saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save credentials'}
        </button>
      </div>
    </div>
  );
}
